import { useState, useEffect, useRef } from 'react';
import { 
  collection, 
  getDocs, 
  doc, 
  updateDoc, 
  query, 
  orderBy,
  setDoc,
  serverTimestamp,
  where,
  deleteDoc,
  deleteField,
  httpsCallable,
  getCountFromServer
} from 'firebase/firestore';
import { ref, deleteObject, getDownloadURL, uploadBytes } from 'firebase/storage';
import { db, storage } from '../config/firebase';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../components/ui/Toast';
import { getFunctions, httpsCallable as callFunction } from 'firebase/functions';
import Card, { CardHeader, CardBody } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';
import { Input } from '../components/ui/FormField';
import UserDetailsModal from '../components/UserDetailsModal';
import { logAdminActivity } from '../services/adminActivityLog';
import { CITIES, cityName } from '../utils/cities';
import { 
  Search, 
  CheckCircle, 
  XCircle,
  Clock,
  Phone, 
  Mail, 
  Calendar,
  Eye,
  FileText,
  Wallet,
  RefreshCw,
  Image as ImageIcon,
  User,
  Upload,
  Ban,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  X,
  Trash2,
  DollarSign,
  MapPin,
  Download
} from 'lucide-react';

const Drivers = () => {
  const [drivers, setDrivers] = useState([]);
  const [filteredDrivers, setFilteredDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCity, setFilterCity] = useState('all');
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [showDocumentsModal, setShowDocumentsModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [loadingAction, setLoadingAction] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [resolvedDocUrls, setResolvedDocUrls] = useState({});
  const [loadingDocUrls, setLoadingDocUrls] = useState(false);
  const [adjustModalDriver, setAdjustModalDriver] = useState(null);
  const [adjustType, setAdjustType] = useState('gift');
  const [adjustAmount, setAdjustAmount] = useState('');
  const [adjustDescription, setAdjustDescription] = useState('');
  const [submittingAdjust, setSubmittingAdjust] = useState(false);
  // Per-driver "max delivery distance" override modal state. Admin
  // types a km value here; saving writes it to
  // users/{uid}.maxDeliveryDistanceOverride and the driver app picks it
  // up in real time via its snapshot listener.
  const [distanceModalDriver, setDistanceModalDriver] = useState(null);
  const [distanceInput, setDistanceInput] = useState('');
  const [submittingDistance, setSubmittingDistance] = useState(false);
  // Global maxDeliveryDistance from app_settings/app_config — shown
  // read-only in the modal so admin sees what the driver would fall
  // back to if the override is cleared.
  const [globalMaxDistance, setGlobalMaxDistance] = useState(null);
  const [uploadDocsDriver, setUploadDocsDriver] = useState(null);
  const [docFiles, setDocFiles] = useState({
    nationalIdFront: null,
    nationalIdBack: null,
    driverLicense: null,
    vehicleRegistration: null,
  });
  const [uploadingDocs, setUploadingDocs] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  // Total orders (ANY status) per driver, keyed by driver id. Computed with a
  // server-side count() only for the drivers on the current page (cached), so
  // the row can show the true total order count instead of the delivered-only
  // counter. undefined = not fetched yet, number = ready.
  const [orderCounts, setOrderCounts] = useState({});
  const driversPerPage = 10;
  const { toast } = useToast();
  const { user: currentAdmin, isMasterAdmin } = useAuth();

  // Extract Firebase Storage path from a download URL, gs:// URI, or plain path
  const extractStoragePath = (url) => {
    if (!url) return null;
    if (url.startsWith('gs://')) {
      return url.replace(/^gs:\/\/[^/]+\//, '');
    }
    if (url.includes('firebasestorage.googleapis.com')) {
      try {
        const match = url.match(/\/o\/([^?]+)/);
        if (match) return decodeURIComponent(match[1]);
      } catch {}
    }
    // Plain storage path (no http/https prefix)
    if (!url.startsWith('http')) {
      return url;
    }
    return null;
  };

  // Get fresh download URLs — handles revoked tokens
  const resolveDocumentUrls = async (driver) => {
    if (!driver?.documents) {
      setLoadingDocUrls(false);
      return;
    }
    setLoadingDocUrls(true);
    const keys = ['nationalIdFront', 'nationalIdBack', 'driverLicense', 'vehicleRegistration'];
    const resolved = {};
    try {
      await Promise.all(keys.map(async (key) => {
        const url = driver.documents[key];
        if (!url) return;
        try {
          const path = extractStoragePath(url);
          if (path) {
            resolved[key] = await getDownloadURL(ref(storage, path));
          } else {
            resolved[key] = url;
          }
        } catch {
          resolved[key] = url;
        }
      }));
    } finally {
      setResolvedDocUrls(resolved);
      setLoadingDocUrls(false);
    }
  };

  useEffect(() => {
    fetchDrivers();
  }, []);

  useEffect(() => {
    filterDrivers();
  }, [drivers, searchTerm, filterStatus, filterCity]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterStatus, filterCity]);

  const fetchDrivers = async () => {
    try {
      setLoading(true);
      const driversRef = collection(db, 'users');
      const q = query(driversRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      
      const driversData = snapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        .filter(user => user.type === 'driver');
      
      setDrivers(driversData);
    } catch (error) {
      console.error('Error fetching drivers:', error);
      toast.error('Failed to fetch drivers');
    } finally {
      setLoading(false);
    }
  };

  const filterDrivers = () => {
    let filtered = [...drivers];

    if (searchTerm) {
      filtered = filtered.filter(driver => 
        driver.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        driver.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        driver.phoneNumber?.includes(searchTerm) ||
        driver.uid?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        driver.id?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filterStatus !== 'all') {
      filtered = filtered.filter(driver => driver.status === filterStatus);
    }

    // Service-city filter. When admin picks a specific city we show only
    // drivers who *explicitly* listed that city in serviceCities. Drivers
    // on ['all'] or legacy (no field) are excluded — admin asked "who
    // picked Aqaba", not "who would receive an Aqaba broadcast".
    if (filterCity !== 'all') {
      filtered = filtered.filter((driver) => {
        const raw = driver.serviceCities;
        if (!Array.isArray(raw)) return false;
        return raw.includes(filterCity);
      });
    }

    setFilteredDrivers(filtered);
  };

  const totalPages = Math.ceil(filteredDrivers.length / driversPerPage);
  const startIndex = (currentPage - 1) * driversPerPage;
  const endIndex = startIndex + driversPerPage;
  const currentDrivers = filteredDrivers.slice(startIndex, endIndex);

  // For the drivers visible on the current page, fetch their TOTAL order count
  // (all statuses) via a server-side count() aggregation — cheap, no doc reads.
  // Results are cached in orderCounts so paging back doesn't refetch.
  useEffect(() => {
    const missing = currentDrivers.filter((d) => orderCounts[d.id] === undefined);
    if (missing.length === 0) return;
    let cancelled = false;
    (async () => {
      const entries = await Promise.all(
        missing.map(async (d) => {
          try {
            const agg = await getCountFromServer(
              query(collection(db, 'orders'), where('driverId', '==', d.id)),
            );
            return [d.id, agg.data().count];
          } catch (e) {
            console.error('order count failed for', d.id, e);
            return [d.id, null]; // null = failed; render falls back gracefully
          }
        }),
      );
      if (!cancelled) {
        setOrderCounts((prev) => {
          const next = { ...prev };
          for (const [id, count] of entries) next[id] = count;
          return next;
        });
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, filteredDrivers]);

  const goToPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  // Export the CURRENT filter view (search + status + city) to CSV.
  // Uses a UTF-8 BOM so Excel opens Arabic driver names correctly.
  const exportToCSV = () => {
    if (filteredDrivers.length === 0) {
      toast.error('No drivers to export');
      return;
    }
    const headers = [
      'Name', 'Phone', 'Email', 'Status', 'Wallet Balance (JOD)',
      'Rating', 'Total Orders', 'Service Cities',
      'Max Distance Override (km)', 'Platform', 'App Version', 'Created At',
    ];
    const rows = filteredDrivers.map((d) => [
      d.name || '',
      d.phoneNumber || '',
      d.email || '',
      d.status || '',
      typeof d.wallet_balance === 'number' ? d.wallet_balance.toFixed(2) : '0.00',
      typeof d.rating === 'number' ? d.rating.toFixed(2) : '',
      // Default to 0 (not empty string) so cells always show a number
      // — the field only exists on drivers who have completed at
      // least one order. Missing = 0.
      d.total_orders_delivered ?? 0,
      Array.isArray(d.serviceCities) ? d.serviceCities.join(' | ') : '',
      typeof d.maxDeliveryDistanceOverride === 'number'
        ? d.maxDeliveryDistanceOverride
        : '',
      d.platform || '',
      d.appVersion || '',
      d.createdAt?.seconds
        ? new Date(d.createdAt.seconds * 1000).toLocaleString()
        : '',
    ]);
    const escape = (cell) => `"${String(cell).replace(/"/g, '""')}"`;
    const csv = [
      headers.map(escape).join(','),
      ...rows.map((r) => r.map(escape).join(',')),
    ].join('\n');
    const bom = '﻿';
    const blob = new Blob([bom + csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `drivers_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
    toast.success(`Exported ${filteredDrivers.length} drivers`);
  };

  const approveDriver = async (driver) => {
    if (!window.confirm(`Are you sure you want to approve ${driver.name}?`)) {
      return;
    }

    try {
      setLoadingAction(true);
      
      const driverRef = doc(db, 'users', driver.id);
      await updateDoc(driverRef, {
        status: 'active',
        approvedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      // Send notification to driver
      try {
        const functions = getFunctions();
        const notifyDriverApproved = callFunction(functions, 'notifyDriverApproved');
        await notifyDriverApproved({
          driverId: driver.id,
          adminName: currentAdmin?.name || 'Admin'
        });
        console.log('Approval notification sent');
      } catch (notifyError) {
        console.warn('Failed to send approval notification:', notifyError);
        // Continue anyway - approval was successful
      }

      await fetchDrivers();
      logAdminActivity({
        actor: currentAdmin, action: 'approve_driver',
        targetType: 'driver', targetId: driver.id || driver.uid,
        targetLabel: driver.name,
      });
      toast.success(`${driver.name} has been approved`);
    } catch (error) {
      console.error('Error approving driver:', error);
      toast.error(`Failed to approve driver: ${error.message}`);
      await fetchDrivers();
    } finally {
      setLoadingAction(false);
    }
  };

  const rejectDriver = async (driver) => {
    if (!window.confirm(`Are you sure you want to reject ${driver.name}? This action will prevent them from being a driver.`)) {
      return;
    }

    try {
      setLoadingAction(true);
      
      const driverRef = doc(db, 'users', driver.id);
      await updateDoc(driverRef, {
        status: 'rejected',
        rejectedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      // Send notification to driver
      try {
        const functions = getFunctions();
        const notifyDriverRejected = callFunction(functions, 'notifyDriverRejected');
        await notifyDriverRejected({
          driverId: driver.id,
          reason: 'Your documents do not meet the required standards',
          adminName: currentAdmin?.name || 'Admin'
        });
        console.log('Rejection notification sent');
      } catch (notifyError) {
        console.warn('Failed to send rejection notification:', notifyError);
        // Continue anyway - rejection was successful
      }

      await fetchDrivers();
      logAdminActivity({
        actor: currentAdmin, action: 'reject_driver',
        targetType: 'driver', targetId: driver.id || driver.uid,
        targetLabel: driver.name,
      });
      toast.success(`${driver.name} has been rejected`);
    } catch (error) {
      console.error('Error rejecting driver:', error);
      toast.error(`Failed to reject driver: ${error.message}`);
      await fetchDrivers();
    } finally {
      setLoadingAction(false);
    }
  };

  const requestDocumentReupload = async (driver) => {
    if (!window.confirm(`Request ${driver.name} to re-upload documents? This will delete their current documents.`)) {
      return;
    }

    try {
      setLoadingAction(true);
      
      if (driver.documents) {
        const deletePromises = [];
        
        if (driver.documents.nationalIdFront) {
          try {
            const frontRef = ref(storage, driver.documents.nationalIdFront);
            deletePromises.push(deleteObject(frontRef));
          } catch (err) {
            console.warn('Failed to delete nationalIdFront:', err);
          }
        }
        
        if (driver.documents.nationalIdBack) {
          try {
            const backRef = ref(storage, driver.documents.nationalIdBack);
            deletePromises.push(deleteObject(backRef));
          } catch (err) {
            console.warn('Failed to delete nationalIdBack:', err);
          }
        }
        
        if (driver.documents.driverLicense) {
          try {
            const licenseRef = ref(storage, driver.documents.driverLicense);
            deletePromises.push(deleteObject(licenseRef));
          } catch (err) {
            console.warn('Failed to delete driverLicense:', err);
          }
        }
        
        if (driver.documents.vehicleRegistration) {
          try {
            const regRef = ref(storage, driver.documents.vehicleRegistration);
            deletePromises.push(deleteObject(regRef));
          } catch (err) {
            console.warn('Failed to delete vehicleRegistration:', err);
          }
        }
        
        await Promise.allSettled(deletePromises);
      }
      
      const driverRef = doc(db, 'users', driver.id);
      await updateDoc(driverRef, {
        status: 'documents_required',
        documents: {
          nationalIdFront: null,
          nationalIdBack: null,
          driverLicense: null,
          vehicleRegistration: null,
          uploadedAt: null
        },
        documentsRequestedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      // Send notification to driver
      try {
        const functions = getFunctions();
        const notifyDriverDocumentsRequired = callFunction(functions, 'notifyDriverDocumentsRequired');
        await notifyDriverDocumentsRequired({
          driverId: driver.id,
          reason: 'Some documents are unclear or do not meet requirements. Please re-upload.',
          adminName: currentAdmin?.name || 'Admin'
        });
        console.log('Documents re-upload notification sent');
      } catch (notifyError) {
        console.warn('Failed to send documents re-upload notification:', notifyError);
        // Continue anyway - document request was successful
      }

      await fetchDrivers();
      logAdminActivity({
        actor: currentAdmin, action: 'request_driver_docs',
        targetType: 'driver', targetId: driver.id || driver.uid,
        targetLabel: driver.name,
      });
      toast.success(`Document re-upload requested from ${driver.name}. Old documents have been deleted.`);
    } catch (error) {
      console.error('Error requesting document re-upload:', error);
      toast.error(`Failed to request documents: ${error.message}`);
      await fetchDrivers();
    } finally {
      setLoadingAction(false);
    }
  };

  const banDriver = async (driver) => {
    if (!window.confirm(`Are you sure you want to ban ${driver.name}? They will no longer be able to accept orders.`)) {
      return;
    }

    try {
      setLoadingAction(true);
      
      const driverRef = doc(db, 'users', driver.id);
      await updateDoc(driverRef, {
        status: 'banned',
        bannedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      try {
        const blockedNumberId = `${driver.phoneNumber}-${Date.now()}`;
        const blockedNumberRef = doc(db, 'BlockedNumbers', blockedNumberId);
        
        await setDoc(blockedNumberRef, {
          number: driver.phoneNumber,
          owner: {
            id: driver.id,
            name: driver.name,
            type: 'driver'
          },
          blockedAt: serverTimestamp(),
          blockedBy: isMasterAdmin ? 'admin_master' : currentAdmin.name,
          blockedByUid: currentAdmin.uid,
          blockedByType: currentAdmin.type,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      } catch (blockError) {
        console.warn('Failed to add to BlockedNumbers:', blockError);
      }

      await fetchDrivers();
      logAdminActivity({
        actor: currentAdmin, action: 'ban_driver',
        targetType: 'driver', targetId: driver.id || driver.uid,
        targetLabel: driver.name,
      });
      toast.success(`${driver.name} has been banned`);
    } catch (error) {
      console.error('Error banning driver:', error);
      toast.error(`Failed to ban driver: ${error.message}`);
      await fetchDrivers();
    } finally {
      setLoadingAction(false);
    }
  };

  const unbanDriver = async (driver) => {
    if (!window.confirm(`Are you sure you want to unban ${driver.name}?`)) {
      return;
    }

    try {
      setLoadingAction(true);
      
      const driverRef = doc(db, 'users', driver.id);
      await updateDoc(driverRef, {
        status: 'active',
        unbannedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      try {
        const blockedNumbersRef = collection(db, 'BlockedNumbers');
        const q = query(blockedNumbersRef, where('number', '==', driver.phoneNumber));
        const snapshot = await getDocs(q);
        
        const deletePromises = snapshot.docs.map(docSnapshot => 
          deleteDoc(doc(db, 'BlockedNumbers', docSnapshot.id))
        );
        await Promise.all(deletePromises);
      } catch (blockError) {
        console.warn('Failed to remove from BlockedNumbers:', blockError);
      }

      await fetchDrivers();
      logAdminActivity({
        actor: currentAdmin, action: 'unban_driver',
        targetType: 'driver', targetId: driver.id || driver.uid,
        targetLabel: driver.name,
      });
      toast.success(`${driver.name} has been unbanned and phone number unblocked`);
    } catch (error) {
      console.error('Error unbanning driver:', error);
      toast.error(`Failed to unban driver: ${error.message}`);
      await fetchDrivers();
    } finally {
      setLoadingAction(false);
    }
  };

  const reactivateDriver = async (driver) => {
    if (!window.confirm(`Are you sure you want to reactivate ${driver.name}?`)) {
      return;
    }

    try {
      setLoadingAction(true);
      
      const driverRef = doc(db, 'users', driver.id);
      await updateDoc(driverRef, {
        status: 'active',
        reactivatedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      await fetchDrivers();
      logAdminActivity({
        actor: currentAdmin, action: 'unban_driver',
        targetType: 'driver', targetId: driver.id || driver.uid,
        targetLabel: driver.name,
      });
      toast.success(`${driver.name} has been reactivated`);
    } catch (error) {
      console.error('Error reactivating driver:', error);
      toast.error(`Failed to reactivate driver: ${error.message}`);
      await fetchDrivers();
    } finally {
      setLoadingAction(false);
    }
  };

  // admin_master-only HARD delete: removes the driver from Firebase Auth
  // AND deletes the Firestore document + subcollections via the
  // `adminDeleteUser` Cloud Function.
  const hardDeleteDriver = async (driver) => {
    if (!isMasterAdmin) return; // UI guards too, but defence in depth
    const confirm1 = window.confirm(
      `⚠️ Permanently delete ${driver.name} (${driver.phoneNumber})?\n\n` +
      `This removes the driver from Firebase Auth AND Firestore (including ` +
      `wallet transactions, addresses, ratings). This cannot be undone.`
    );
    if (!confirm1) return;
    const typed = window.prompt(`Type DELETE to confirm deletion of ${driver.name}:`);
    if (typed !== 'DELETE') return;

    try {
      setLoadingAction(true);
      const fns = getFunctions();
      const adminDeleteUser = callFunction(fns, 'adminDeleteUser');
      await adminDeleteUser({ userId: driver.id });
      logAdminActivity({
        actor: currentAdmin, action: 'delete_driver',
        targetType: 'driver', targetId: driver.id || driver.uid,
        targetLabel: driver.name,
      });
      toast.success(`${driver.name} has been permanently deleted`);
      await fetchDrivers();
    } catch (error) {
      console.error('Error deleting driver:', error);
      toast.error(`Failed to delete driver: ${error.message}`);
    } finally {
      setLoadingAction(false);
    }
  };

  const openAdjustModal = (driver) => {
    setAdjustModalDriver(driver);
    setAdjustType('gift');
    setAdjustAmount('');
    setAdjustDescription('');
  };

  const closeAdjustModal = () => {
    if (submittingAdjust) return;
    setAdjustModalDriver(null);
  };

  const submitAdjust = async () => {
    if (!isMasterAdmin || !adjustModalDriver) return;
    const numeric = Number(adjustAmount);
    if (!Number.isFinite(numeric) || numeric <= 0) {
      toast.error('Enter a valid positive amount');
      return;
    }
    try {
      setSubmittingAdjust(true);
      const fns = getFunctions();
      const adjust = callFunction(fns, 'adminAdjustDriverBalance');
      await adjust({
        driverId: adjustModalDriver.id,
        type: adjustType,
        amount: numeric,
        description: adjustDescription.trim() || null,
      });
      const isCredit = ['gift', 'deposit', 'refund'].includes(adjustType);
      logAdminActivity({
        actor: currentAdmin,
        action: 'adjust_driver_balance',
        targetType: 'driver',
        targetId: adjustModalDriver.id,
        targetLabel: `${adjustModalDriver.name} · ${isCredit ? '+' : '-'}${numeric} JOD (${adjustType})`,
      });
      toast.success(
        `${isCredit ? 'Credited' : 'Debited'} ${numeric} JOD ${isCredit ? 'to' : 'from'} ${adjustModalDriver.name}`,
      );
      setAdjustModalDriver(null);
      await fetchDrivers();
    } catch (error) {
      console.error('Error adjusting balance:', error);
      toast.error(`Failed: ${error.message}`);
    } finally {
      setSubmittingAdjust(false);
    }
  };

  const viewDriverDetails = (driver) => {
    setSelectedDriver(driver);
    setShowDetailsModal(true);
  };

  // Open the distance-override modal. Pre-fill the input with the
  // driver's current override (blank if none set), and lazy-load the
  // global fallback value from app_settings for context.
  const openDistanceModal = async (driver) => {
    const current = driver.maxDeliveryDistanceOverride;
    setDistanceInput(
      typeof current === 'number' && current > 0 ? String(current) : '',
    );
    setDistanceModalDriver(driver);
    if (globalMaxDistance == null) {
      try {
        const cfgSnap = await getDocs(
          query(collection(db, 'app_settings'), where('__name__', '==', 'app_config')),
        );
        cfgSnap.forEach((d) => {
          const raw = d.data()?.maxDeliveryDistance;
          if (typeof raw === 'number') setGlobalMaxDistance(raw);
        });
      } catch (e) {
        console.warn('Failed to load global maxDeliveryDistance:', e);
      }
    }
  };

  const closeDistanceModal = () => {
    if (submittingDistance) return;
    setDistanceModalDriver(null);
  };

  const submitDistanceOverride = async () => {
    if (!distanceModalDriver) return;
    const numeric = Number(distanceInput);
    if (!Number.isFinite(numeric) || numeric <= 0) {
      toast.error('Enter a positive distance in km');
      return;
    }
    try {
      setSubmittingDistance(true);
      await updateDoc(doc(db, 'users', distanceModalDriver.id), {
        maxDeliveryDistanceOverride: numeric,
        updatedAt: serverTimestamp(),
      });
      logAdminActivity({
        actor: currentAdmin,
        action: 'set_driver_max_distance',
        targetType: 'driver',
        targetId: distanceModalDriver.id,
        targetLabel: `${distanceModalDriver.name} · ${numeric} km`,
      });
      toast.success(`Max distance set to ${numeric} km for ${distanceModalDriver.name}`);
      setDistanceModalDriver(null);
      await fetchDrivers();
    } catch (error) {
      console.error('Error setting distance override:', error);
      toast.error(`Failed: ${error.message}`);
    } finally {
      setSubmittingDistance(false);
    }
  };

  const clearDistanceOverride = async () => {
    if (!distanceModalDriver) return;
    try {
      setSubmittingDistance(true);
      await updateDoc(doc(db, 'users', distanceModalDriver.id), {
        // deleteField() removes the property entirely so the driver
        // app's fallback (`override ?? global`) kicks back in.
        maxDeliveryDistanceOverride: deleteField(),
        updatedAt: serverTimestamp(),
      });
      logAdminActivity({
        actor: currentAdmin,
        action: 'clear_driver_max_distance',
        targetType: 'driver',
        targetId: distanceModalDriver.id,
        targetLabel: `${distanceModalDriver.name} · cleared → global`,
      });
      toast.success(`Override cleared for ${distanceModalDriver.name}`);
      setDistanceModalDriver(null);
      await fetchDrivers();
    } catch (error) {
      console.error('Error clearing distance override:', error);
      toast.error(`Failed: ${error.message}`);
    } finally {
      setSubmittingDistance(false);
    }
  };

  const viewDocuments = (driver) => {
    setSelectedDriver(driver);
    setResolvedDocUrls({});
    console.log('📄 RAW documents for', driver.name, ':', JSON.stringify(driver.documents, null, 2));
    resolveDocumentUrls(driver);
    setShowDocumentsModal(true);
  };

  const openImage = (imageUrl, title) => {
    setSelectedImage({ url: imageUrl, title });
    setShowImageModal(true);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    let date;
    if (timestamp.toDate) {
      date = timestamp.toDate();
    } else if (typeof timestamp === 'string') {
      date = new Date(timestamp);
    } else {
      return 'N/A';
    }
    
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge variant="success">Active</Badge>;
      case 'pending':
        return <Badge variant="warning">Pending</Badge>;
      case 'rejected':
        return <Badge variant="error">Rejected</Badge>;
      case 'documents_required':
        return <Badge variant="warning">Docs Required</Badge>;
      case 'banned':
        return <Badge variant="error">Banned</Badge>;
      case 'incomplete':
        return <Badge variant="secondary">Incomplete</Badge>;
      default:
        return <Badge variant="default">{status}</Badge>;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'JOD',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Drivers</h1>
        <p className="text-gray-600 mt-1">Manage and monitor driver accounts</p>
      </div>

      {/* Filters and Search */}
      <Card>
        <CardBody>
          {/* Layout:
              Row 1 — search input (full width) + right-side Export & Refresh.
              Row 2 — status filter pills + city dropdown (wrap freely).
              Splitting the two rows fixes the previous bug where the
              Export/Refresh buttons stretched vertically to match the
              wrapping pill row. */}
          <div className="space-y-3">
            <div className="flex flex-col md:flex-row gap-3 md:items-center">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <Input
                    type="text"
                    placeholder="Search by name, email, phone, or UID..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={exportToCSV}
                  disabled={loading || filteredDrivers.length === 0}
                  title="Export the current filter view to a CSV file (opens in Excel)"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export
                </Button>
                <Button
                  variant="outline"
                  onClick={fetchDrivers}
                  disabled={loading}
                >
                  <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                </Button>
              </div>
            </div>

            <div className="flex gap-2 flex-wrap">
              <Button
                variant={filterStatus === 'all' ? 'primary' : 'outline'}
                onClick={() => setFilterStatus('all')}
              >
                All ({drivers.length})
              </Button>
              <Button
                variant={filterStatus === 'active' ? 'primary' : 'outline'}
                onClick={() => setFilterStatus('active')}
              >
                Active ({drivers.filter(d => d.status === 'active').length})
              </Button>
              <Button
                variant={filterStatus === 'pending' ? 'primary' : 'outline'}
                onClick={() => setFilterStatus('pending')}
              >
                Pending ({drivers.filter(d => d.status === 'pending').length})
              </Button>
              <Button
                variant={filterStatus === 'documents_required' ? 'primary' : 'outline'}
                onClick={() => setFilterStatus('documents_required')}
              >
                Docs Required ({drivers.filter(d => d.status === 'documents_required').length})
              </Button>
              <Button
                variant={filterStatus === 'banned' ? 'primary' : 'outline'}
                onClick={() => setFilterStatus('banned')}
              >
                Banned ({drivers.filter(d => d.status === 'banned').length})
              </Button>
              <Button
                variant={filterStatus === 'rejected' ? 'primary' : 'outline'}
                onClick={() => setFilterStatus('rejected')}
              >
                Rejected ({drivers.filter(d => d.status === 'rejected').length})
              </Button>
              <Button
                variant={filterStatus === 'incomplete' ? 'primary' : 'outline'}
                onClick={() => setFilterStatus('incomplete')}
              >
                Incomplete ({drivers.filter(d => d.status === 'incomplete').length})
              </Button>

              <CityFilterDropdown
                value={filterCity}
                onChange={setFilterCity}
                drivers={drivers}
              />
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Drivers Table */}
      <Card>
        <CardHeader>
          <h2 className="text-xl font-semibold">
            Driver List ({filteredDrivers.length})
          </h2>
        </CardHeader>
        <CardBody className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Driver
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Stats
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Joined
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {currentDrivers.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                      No drivers found
                    </td>
                  </tr>
                ) : (
                  currentDrivers.map((driver) => (
                    <tr key={driver.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="h-10 w-10 flex-shrink-0">
                            {driver.profilePhoto ? (
                              <img
                                className="h-10 w-10 rounded-full object-cover cursor-pointer hover:ring-2 hover:ring-primary-500"
                                src={driver.profilePhoto}
                                alt={driver.name}
                                onClick={() => openImage(driver.profilePhoto, driver.name)}
                              />
                            ) : (
                              <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                                <User className="h-6 w-6 text-gray-400" />
                              </div>
                            )}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900 font-arabic">
                              {driver.name}
                            </div>
                            <div className="text-sm text-gray-500">
                              UID: {driver.uid || driver.id}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="space-y-1">
                          <div className="flex items-center text-sm text-gray-900">
                            <Mail className="w-4 h-4 mr-2 text-gray-400" />
                            {driver.email}
                          </div>
                          <div className="flex items-center text-sm text-gray-500">
                            <Phone className="w-4 h-4 mr-2 text-gray-400" />
                            {driver.phoneNumber}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(driver.status)}
                      </td>
                      <td className="px-6 py-4">
                        <div className="space-y-1 text-sm">
                          <div className="flex items-center text-gray-900">
                            <FileText className="w-4 h-4 mr-2 text-gray-400" />
                            {orderCounts[driver.id] === undefined
                              ? '…'
                              : orderCounts[driver.id] === null
                              ? (driver.total_orders_delivered || 0)
                              : orderCounts[driver.id]}{' '}
                            orders
                          </div>
                          <div className="flex items-center text-gray-500">
                            <Wallet className="w-4 h-4 mr-2 text-gray-400" />
                            {formatCurrency(driver.wallet_balance)}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-500">
                          <Calendar className="w-4 h-4 mr-2" />
                          {formatDate(driver.createdAt)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => viewDriverDetails(driver)}
                          disabled={loadingAction}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Details
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="border-t border-gray-200 px-6 py-4 bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
                  <span className="font-medium">{Math.min(endIndex, filteredDrivers.length)}</span> of{' '}
                  <span className="font-medium">{filteredDrivers.length}</span> drivers
                </div>
                
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="p-2 rounded-lg border border-gray-300 hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  
                  <div className="flex items-center gap-1">
                    {[...Array(totalPages)].map((_, i) => {
                      const page = i + 1;
                      if (
                        page === 1 ||
                        page === totalPages ||
                        (page >= currentPage - 1 && page <= currentPage + 1)
                      ) {
                        return (
                          <button
                            key={page}
                            onClick={() => goToPage(page)}
                            className={`min-w-[40px] px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                              currentPage === page
                                ? 'bg-primary-600 text-white'
                                : 'border border-gray-300 hover:bg-white text-gray-700'
                            }`}
                          >
                            {page}
                          </button>
                        );
                      } else if (page === currentPage - 2 || page === currentPage + 2) {
                        return (
                          <span key={page} className="px-2 text-gray-400">
                            ...
                          </span>
                        );
                      }
                      return null;
                    })}
                  </div>
                  
                  <button
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="p-2 rounded-lg border border-gray-300 hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Documents Modal */}
      {showDocumentsModal && selectedDriver && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <div>
                <h3 className="text-xl font-semibold text-gray-900">
                  Driver Documents - {selectedDriver.name}
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  UID: {selectedDriver.uid || selectedDriver.id}
                </p>
              </div>
              <button
                onClick={() => {
                  Object.values(resolvedDocUrls).forEach(u => { if (u?.startsWith('blob:')) URL.revokeObjectURL(u); });
                  setShowDocumentsModal(false);
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Driver Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm text-gray-500">Email</p>
                  <p className="text-sm font-medium text-gray-900">{selectedDriver.email}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Phone</p>
                  <p className="text-sm font-medium text-gray-900">{selectedDriver.phoneNumber}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Status</p>
                  <div className="mt-1">{getStatusBadge(selectedDriver.status)}</div>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Joined</p>
                  <p className="text-sm font-medium text-gray-900">{formatDate(selectedDriver.createdAt)}</p>
                </div>
              </div>

              {/* Documents */}
              <div className="space-y-4">
                <h4 className="text-lg font-semibold text-gray-900">Uploaded Documents</h4>
                
                {selectedDriver.documents ? (
                  loadingDocUrls ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                      <span className="ml-3 text-gray-500">Loading documents...</span>
                    </div>
                  ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[
                      { key: 'nationalIdFront', label: 'National ID (Front)' },
                      { key: 'nationalIdBack',  label: 'National ID (Back)'  },
                      { key: 'driverLicense',   label: 'Driver License'       },
                      { key: 'vehicleRegistration', label: 'Vehicle Registration' },
                    ].map(({ key, label }) => {
                      const url = resolvedDocUrls[key];
                      return (
                        <div key={key} className="border border-gray-200 rounded-lg p-4">
                          <h5 className="text-sm font-medium text-gray-900 mb-2 flex items-center">
                            <FileText className="w-4 h-4 mr-2" />
                            {label}
                          </h5>
                          {url ? (
                            <div className="space-y-2">
                              <img
                                src={url}
                                alt={label}
                                className="w-full h-48 object-cover rounded cursor-pointer hover:opacity-80 transition-opacity"
                                onClick={() => openImage(url, label)}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.parentNode.querySelector('.doc-error-fallback').style.display = 'flex';
                                  console.error(`[Docs] ❌ Image render failed for ${label}. URL was:`, url);
                                }}
                              />
                              <div className="doc-error-fallback h-48 bg-gray-100 rounded flex-col gap-2 items-center justify-center" style={{display:'none'}}>
                                <ImageIcon className="w-8 h-8 text-gray-400" />
                                <p className="text-gray-400 text-sm">Could not load image</p>
                              </div>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openImage(url, label)}
                                className="w-full"
                              >
                                <Eye className="w-4 h-4 mr-2" />
                                View Full Size
                              </Button>
                            </div>
                          ) : (
                            <div className="h-48 bg-gray-100 rounded flex items-center justify-center">
                              <p className="text-gray-400 text-sm">Not uploaded</p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  )
                ) : (
                  <div className="text-center py-8 bg-gray-50 rounded-lg">
                    <FileText className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                    <p className="text-gray-500">No documents uploaded yet</p>
                  </div>
                )}

                {/* Document Upload Date */}
                {selectedDriver.documents?.uploadedAt && (
                  <div className="text-sm text-gray-500">
                    Documents uploaded: {formatDate(selectedDriver.documents.uploadedAt)}
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
                <Button
                  variant="outline"
                  onClick={() => setShowDocumentsModal(false)}
                >
                  Close
                </Button>
                
                {(selectedDriver.status === 'pending' || selectedDriver.status === 'documents_required') && (
                  <>
                    <Button
                      variant="warning"
                      onClick={() => {
                        setShowDocumentsModal(false);
                        requestDocumentReupload(selectedDriver);
                      }}
                      disabled={loadingAction}
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Request Re-upload
                    </Button>
                    <Button
                      variant="success"
                      onClick={() => {
                        setShowDocumentsModal(false);
                        approveDriver(selectedDriver);
                      }}
                      disabled={loadingAction}
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Approve Driver
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Image Modal */}
      {showImageModal && selectedImage && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4"
          onClick={() => setShowImageModal(false)}
        >
          <div className="relative max-w-7xl max-h-full">
            <button
              onClick={() => setShowImageModal(false)}
              className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors"
            >
              <X className="w-8 h-8" />
            </button>
            
            {selectedImage.title && (
              <div className="absolute -top-12 left-0 text-white text-lg font-medium">
                {selectedImage.title}
              </div>
            )}
            
            <img
              src={selectedImage.url}
              alt={selectedImage.title || 'Image'}
              className="max-w-full max-h-[90vh] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}

      {adjustModalDriver && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          onClick={closeAdjustModal}
        >
          <div
            className="bg-white rounded-2xl shadow-xl w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-primary-600" />
                Adjust Balance
              </h3>
              <button
                onClick={closeAdjustModal}
                className="p-1 rounded hover:bg-slate-100"
                disabled={submittingAdjust}
              >
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-sm text-slate-600">Driver</p>
                <p className="font-semibold text-slate-900">{adjustModalDriver.name}</p>
                <p className="text-xs text-slate-500">{adjustModalDriver.phoneNumber}</p>
                <p className="text-sm text-slate-600 mt-2">
                  Current balance:{' '}
                  <span className="font-bold text-slate-900">
                    {Number(adjustModalDriver.wallet_balance || 0).toFixed(2)} JOD
                  </span>
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Type *</label>
                <select
                  value={adjustType}
                  onChange={(e) => setAdjustType(e.target.value)}
                  disabled={submittingAdjust}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                >
                  <optgroup label="Credit (add to balance)">
                    <option value="gift">Gift</option>
                    <option value="deposit">Deposit / Top-up</option>
                    <option value="refund">Refund</option>
                  </optgroup>
                  <optgroup label="Debit (subtract from balance)">
                    <option value="deduction">Deduction</option>
                    <option value="withdraw">Withdraw</option>
                  </optgroup>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Amount (JOD) *</label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={adjustAmount}
                  onChange={(e) => setAdjustAmount(e.target.value)}
                  disabled={submittingAdjust}
                  placeholder="e.g. 5.00"
                />
                {Number(adjustAmount) > 0 && (
                  <p className="text-xs text-slate-500 mt-1">
                    New balance will be:{' '}
                    <span className="font-semibold text-slate-700">
                      {(
                        Number(adjustModalDriver.wallet_balance || 0) +
                        (['gift', 'deposit', 'refund'].includes(adjustType) ? 1 : -1) *
                          Number(adjustAmount)
                      ).toFixed(2)}{' '}
                      JOD
                    </span>
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Description (optional)
                </label>
                <textarea
                  value={adjustDescription}
                  onChange={(e) => setAdjustDescription(e.target.value)}
                  disabled={submittingAdjust}
                  rows={3}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
                  placeholder="Reason / note for this adjustment (shown to driver)"
                />
              </div>
            </div>

            <div className="px-6 py-4 border-t flex justify-end gap-2">
              <Button variant="outline" onClick={closeAdjustModal} disabled={submittingAdjust}>
                Cancel
              </Button>
              <Button variant="primary" onClick={submitAdjust} disabled={submittingAdjust}>
                {submittingAdjust ? 'Submitting...' : 'Confirm'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Per-driver max delivery distance override modal. Writes to
          users/{uid}.maxDeliveryDistanceOverride; the driver app's
          snapshot listener picks it up in real time and re-filters
          the visible available-orders list without a restart. */}
      {distanceModalDriver && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-primary-600" />
                Delivery Range Override
              </h3>
              <button onClick={closeDistanceModal} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="rounded-lg bg-slate-50 p-3">
                <p className="font-semibold text-slate-900">{distanceModalDriver.name}</p>
                <p className="text-xs text-slate-500">{distanceModalDriver.phoneNumber}</p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-lg border border-slate-200 p-3">
                  <p className="text-xs text-slate-500 uppercase tracking-wide">Global default</p>
                  <p className="text-lg font-bold text-slate-900">
                    {globalMaxDistance != null ? `${globalMaxDistance} km` : '—'}
                  </p>
                </div>
                <div className="rounded-lg border border-slate-200 p-3">
                  <p className="text-xs text-slate-500 uppercase tracking-wide">Current override</p>
                  <p className="text-lg font-bold text-slate-900">
                    {typeof distanceModalDriver.maxDeliveryDistanceOverride === 'number' &&
                    distanceModalDriver.maxDeliveryDistanceOverride > 0
                      ? `${distanceModalDriver.maxDeliveryDistanceOverride} km`
                      : <span className="text-slate-400 text-sm font-medium">Using global</span>}
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  New max distance (km)
                </label>
                <p className="text-xs text-slate-500 mb-2">
                  Applies only to this driver. Change hits their app in real time —
                  orders beyond this distance disappear from their available list.
                </p>
                <Input
                  type="number"
                  step="0.1"
                  min="0.1"
                  value={distanceInput}
                  onChange={(e) => setDistanceInput(e.target.value)}
                  placeholder="e.g. 20"
                  disabled={submittingDistance}
                />
              </div>
            </div>
            <div className="px-6 py-4 border-t flex justify-between items-center gap-2 flex-wrap">
              <Button
                variant="outline"
                onClick={clearDistanceOverride}
                disabled={submittingDistance ||
                  !(typeof distanceModalDriver.maxDeliveryDistanceOverride === 'number' &&
                    distanceModalDriver.maxDeliveryDistanceOverride > 0)}
                title="Remove the override so this driver falls back to the global default"
              >
                Clear override
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={closeDistanceModal} disabled={submittingDistance}>
                  Cancel
                </Button>
                <Button variant="primary" onClick={submitDistanceOverride} disabled={submittingDistance}>
                  {submittingDistance ? 'Saving...' : 'Save'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showDetailsModal && selectedDriver && (
        <UserDetailsModal
          user={selectedDriver}
          onClose={() => setShowDetailsModal(false)}
          actions={
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setShowDetailsModal(false);
                  viewDocuments(selectedDriver);
                }}
                disabled={loadingAction}
              >
                <FileText className="w-4 h-4 mr-1" />
                Documents
              </Button>

              {(selectedDriver.status === 'pending' ||
                selectedDriver.status === 'documents_required') && (
                <>
                  <Button
                    variant="success"
                    size="sm"
                    onClick={() => approveDriver(selectedDriver)}
                    disabled={loadingAction}
                  >
                    <CheckCircle className="w-4 h-4 mr-1" />
                    Approve
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => rejectDriver(selectedDriver)}
                    disabled={loadingAction}
                  >
                    <XCircle className="w-4 h-4 mr-1" />
                    Reject
                  </Button>
                  <Button
                    variant="warning"
                    size="sm"
                    onClick={() => requestDocumentReupload(selectedDriver)}
                    disabled={loadingAction}
                  >
                    <Upload className="w-4 h-4 mr-1" />
                    Request Re-upload
                  </Button>
                </>
              )}

              {selectedDriver.status === 'rejected' && (
                <Button
                  variant="success"
                  size="sm"
                  onClick={() => reactivateDriver(selectedDriver)}
                  disabled={loadingAction}
                >
                  <CheckCircle className="w-4 h-4 mr-1" />
                  Reactivate
                </Button>
              )}

              {selectedDriver.status === 'active' && (
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => banDriver(selectedDriver)}
                  disabled={loadingAction}
                >
                  <Ban className="w-4 h-4 mr-1" />
                  Ban
                </Button>
              )}

              {selectedDriver.status === 'banned' && (
                <Button
                  variant="success"
                  size="sm"
                  onClick={() => unbanDriver(selectedDriver)}
                  disabled={loadingAction}
                >
                  <CheckCircle className="w-4 h-4 mr-1" />
                  Unban
                </Button>
              )}

              {isMasterAdmin && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setShowDetailsModal(false);
                    openAdjustModal(selectedDriver);
                  }}
                  disabled={loadingAction}
                  title="Add or deduct balance manually"
                >
                  <DollarSign className="w-4 h-4 mr-1" />
                  Adjust Balance
                </Button>
              )}

              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setShowDetailsModal(false);
                  openDistanceModal(selectedDriver);
                }}
                disabled={loadingAction}
                title="Set a per-driver max delivery distance (overrides the global setting)"
              >
                <MapPin className="w-4 h-4 mr-1" />
                Delivery Range
              </Button>

              {isMasterAdmin && (
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => hardDeleteDriver(selectedDriver)}
                  disabled={loadingAction}
                  title="Permanently delete from Auth + Firestore"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Delete
                </Button>
              )}
            </div>
          }
        />
      )}
    </div>
  );
};

export default Drivers;

/**
 * City filter as a button-pill that opens a popover menu so it visually
 * matches the status filter buttons (rounded, primary when active,
 * outline when inactive). A plain <select> looked out of place next to
 * them.
 */
function CityFilterDropdown({ value, onChange, drivers }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const active = value !== 'all';
  const label = active ? cityName(value, 'en') : 'All cities';

  // Count drivers per city the same way filterDrivers does, so admin
  // sees how many results each option will produce.
  const cityCount = (key) =>
    drivers.filter((d) => Array.isArray(d.serviceCities) && d.serviceCities.includes(key)).length;

  return (
    <div ref={rootRef} className="relative">
      <Button
        variant={active ? 'primary' : 'outline'}
        onClick={() => setOpen((v) => !v)}
        rightIcon={<ChevronDown className="w-4 h-4 opacity-70" />}
      >
        {label}
      </Button>

      {open && (
        <div className="absolute right-0 mt-2 w-56 bg-white border border-slate-200 rounded-xl shadow-lg z-30 py-1 max-h-80 overflow-y-auto">
          <CityOption
            isActive={value === 'all'}
            onClick={() => { onChange('all'); setOpen(false); }}
            label="All cities"
            count={drivers.length}
          />
          <div className="my-1 border-t border-slate-100" />
          {CITIES.map((c) => (
            <CityOption
              key={c.key}
              isActive={value === c.key}
              onClick={() => { onChange(c.key); setOpen(false); }}
              label={cityName(c.key, 'en')}
              count={cityCount(c.key)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function CityOption({ isActive, onClick, label, count }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-sm rounded-lg transition ${
        isActive
          ? 'bg-primary-50 text-primary-700 font-semibold'
          : 'text-slate-700 hover:bg-slate-50'
      }`}
    >
      <span>{label}</span>
      <span className="text-xs text-slate-400">{count}</span>
    </button>
  );
}